import { API } from '../global/global';

export interface NotificationStats {
  tickets: {
    total: number;
    successful: number;
    failed: number;
    pending: number;
  };
  receipts: {
    total: number;
    delivered: number;
    failed: number;
  };
  invalid_tokens: {
    total: number;
    removed: number;
    pending_removal: number;
  };
  active_tokens: number;
}

export interface NotificationTicket {
  id: number;
  ticket_id: string;
  push_token: string;
  title: string;
  body: string;
  status: string;
  error_code?: string;
  error_message?: string;
  sent_at: string;
  receipt?: {
    status: string;
    delivered_at?: string;
  };
}

export interface InvalidToken {
  id: number;
  token: string;
  usuario?: {
    nombre: string;
    apellido_paterno: string;
    correo: string;
  };
  error_type: string;
  error_message: string;
  detected_at: string;
  removed_at?: string;
}

export interface DailyStat {
  date: string;
  total_sent: number;
  successful: number;
  failed: number;
}

export interface ErrorStat {
  error_code: string;
  count: number;
}

class NotificationStatsService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  async getStats(days: number = 7): Promise<NotificationStats> {
    const response = await fetch(`${API}/notifications/stats?days=${days}`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Error al obtener estadísticas');
    }
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Error al obtener estadísticas');
    }
    
    return data.data;
  }

  async getTickets(params: {
    days?: number;
    status?: string;
    page?: number;
    per_page?: number;
  } = {}): Promise<{ data: NotificationTicket[]; total: number; current_page: number }> {
    const queryParams = new URLSearchParams();
    
    if (params.days) queryParams.append('days', params.days.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.per_page) queryParams.append('per_page', params.per_page.toString());

    const response = await fetch(`${API}/notifications/tickets?${queryParams}`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Error al obtener tickets');
    }
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Error al obtener tickets');
    }
    
    return data.data;
  }

  async getInvalidTokens(params: {
    days?: number;
    page?: number;
    per_page?: number;
  } = {}): Promise<{ data: InvalidToken[]; total: number; current_page: number }> {
    const queryParams = new URLSearchParams();
    
    if (params.days) queryParams.append('days', params.days.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.per_page) queryParams.append('per_page', params.per_page.toString());

    const response = await fetch(`${API}/notifications/invalid-tokens?${queryParams}`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Error al obtener tokens inválidos');
    }
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Error al obtener tokens inválidos');
    }
    
    return data.data;
  }

  async getDailyStats(days: number = 30): Promise<DailyStat[]> {
    const response = await fetch(`${API}/notifications/daily-stats?days=${days}`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Error al obtener estadísticas diarias');
    }
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Error al obtener estadísticas diarias');
    }
    
    return data.data;
  }

  async getErrorStats(days: number = 7): Promise<ErrorStat[]> {
    const response = await fetch(`${API}/notifications/error-stats?days=${days}`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Error al obtener estadísticas de errores');
    }
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Error al obtener estadísticas de errores');
    }
    
    return data.data;
  }

  async getTokenSummary(): Promise<{
    active_tokens: number;
    invalid_tokens: {
      today: number;
      this_week: number;
      this_month: number;
    };
  }> {
    const response = await fetch(`${API}/notifications/token-summary`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Error al obtener resumen de tokens');
    }
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Error al obtener resumen de tokens');
    }
    
    return data.data;
  }

  async processReceipts(limit: number = 100): Promise<{ processed: number; message: string }> {
    const response = await fetch(`${API}/notifications/process-receipts`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ limit })
    });
    
    if (!response.ok) {
      throw new Error('Error al procesar receipts');
    }
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Error al procesar receipts');
    }
    
    return {
      processed: data.processed,
      message: data.message
    };
  }

  async cleanupInvalidTokens(days: number = 7): Promise<{ tokens_cleaned: number; message: string }> {
    const response = await fetch(`${API}/notifications/cleanup-invalid-tokens`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ days })
    });
    
    if (!response.ok) {
      throw new Error('Error al limpiar tokens inválidos');
    }
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Error al limpiar tokens inválidos');
    }
    
    return {
      tokens_cleaned: data.tokens_cleaned,
      message: data.message
    };
  }

  async getDeliveryStats(days: number = 7): Promise<Array<{ status: string; count: number }>> {
    const response = await fetch(`${API}/notifications/delivery-stats?days=${days}`, {
      headers: this.getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Error al obtener estadísticas de entrega');
    }
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Error al obtener estadísticas de entrega');
    }
    
    return data.data;
  }
}

export const notificationStatsService = new NotificationStatsService();
